import type { ConditionGrade, OperatorCreateSubmissionInput, SessionUser, TitleStatus } from '@motoveo/shared';
import { CONDITION_GRADES, TITLE_STATUSES } from '@motoveo/shared';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { Layout } from '../components/Layout';
import { dollarsToCents } from '../lib/format';

export function IntakeFormPage({ user }: { user: SessionUser }) {
  const navigate = useNavigate();

  const [sellerName, setSellerName] = useState('');
  const [sellerPhone, setSellerPhone] = useState('');
  const [sellerEmail, setSellerEmail] = useState('');
  const [sellerState, setSellerState] = useState('');
  const [region, setRegion] = useState('');
  const [rawVin, setRawVin] = useState('');
  const [mileage, setMileage] = useState('');
  const [asking, setAsking] = useState('');
  const [trade, setTrade] = useState('');
  const [grade, setGrade] = useState<ConditionGrade | ''>('');
  const [title, setTitle] = useState<TitleStatus | ''>('');
  const [accident, setAccident] = useState(false);
  const [mods, setMods] = useState(false);
  const [notes, setNotes] = useState('');
  const [intakeNotes, setIntakeNotes] = useState('');
  const [pewcConsent, setPewcConsent] = useState(false);

  const create = useMutation({
    mutationFn: () => {
      const payload: OperatorCreateSubmissionInput = {
        source: 'operator_manual',
        raw_vin: rawVin.trim().toUpperCase(),
        pewc_consent: pewcConsent,
        seller_name: sellerName.trim() || undefined,
        seller_phone_e164: sellerPhone.trim() || undefined,
        seller_email: sellerEmail.trim() || undefined,
        seller_state: sellerState.trim() ? sellerState.trim().toUpperCase() : undefined,
        seller_region_label: region.trim() || undefined,
        stated_mileage: mileage.trim() ? Number(mileage.trim()) : undefined,
        asking_price_cents: dollarsToCents(asking) ?? undefined,
        trade_target_cents: dollarsToCents(trade) ?? undefined,
        condition_grade: grade || undefined,
        title_status: title || undefined,
        accident_disclosed: accident,
        has_modifications: mods,
        condition_notes: notes.trim() || undefined,
        intake_notes: intakeNotes.trim() || undefined,
      };
      return api.submissions.create(payload);
    },
    onSuccess: (res) => navigate(`/submissions/${res.id}`),
  });

  const vinLen = rawVin.replace(/\s/g, '').length;

  return (
    <Layout user={user}>
      <h2>New intake (operator manual)</h2>
      <form
        className="card form"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <div className="form-grid">
          <label>
            VIN *
            <input value={rawVin} onChange={(e) => setRawVin(e.target.value)} required maxLength={17} placeholder="WP0AC2A94RS263159" />
            <span className="hint">{vinLen}/17</span>
          </label>
          <label>
            Seller name
            <input value={sellerName} onChange={(e) => setSellerName(e.target.value)} />
          </label>
          <label>
            Seller phone (E.164)
            <input value={sellerPhone} onChange={(e) => setSellerPhone(e.target.value)} placeholder="+13055551234" />
          </label>
          <label>
            Seller email
            <input type="email" value={sellerEmail} onChange={(e) => setSellerEmail(e.target.value)} />
          </label>
          <label>
            State
            <input value={sellerState} onChange={(e) => setSellerState(e.target.value)} maxLength={2} placeholder="FL" />
          </label>
          <label>
            Region label
            <input value={region} onChange={(e) => setRegion(e.target.value)} placeholder="SoCal" />
          </label>
          <label>
            Mileage
            <input inputMode="numeric" value={mileage} onChange={(e) => setMileage(e.target.value)} />
          </label>
          <label>
            Asking price ($)
            <input inputMode="decimal" value={asking} onChange={(e) => setAsking(e.target.value)} placeholder="210000" />
          </label>
          <label>
            Trade target ($)
            <input inputMode="decimal" value={trade} onChange={(e) => setTrade(e.target.value)} />
          </label>
          <label>
            Condition grade
            <select value={grade} onChange={(e) => setGrade(e.target.value as ConditionGrade | '')}>
              <option value="">—</option>
              {CONDITION_GRADES.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>
          </label>
          <label>
            Title status
            <select value={title} onChange={(e) => setTitle(e.target.value as TitleStatus | '')}>
              <option value="">—</option>
              {TITLE_STATUSES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="checks">
          <label className="check">
            <input type="checkbox" checked={accident} onChange={(e) => setAccident(e.target.checked)} /> Accident disclosed
          </label>
          <label className="check">
            <input type="checkbox" checked={mods} onChange={(e) => setMods(e.target.checked)} /> Has modifications
          </label>
        </div>

        <label>
          Condition notes
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
        </label>
        <label>
          Intake notes (internal)
          <textarea value={intakeNotes} onChange={(e) => setIntakeNotes(e.target.value)} rows={2} />
        </label>

        <label className="check consent">
          <input type="checkbox" checked={pewcConsent} onChange={(e) => setPewcConsent(e.target.checked)} />
          Seller granted PEWC consent (records a consent row with capture_source
          <code> operator_manual</code>; requires a phone number)
        </label>

        <div className="row-between">
          <button type="submit" disabled={create.isPending || vinLen !== 17}>
            {create.isPending ? 'Creating…' : 'Create submission'}
          </button>
          {create.isError ? <span className="error">{(create.error as Error).message}</span> : null}
        </div>
      </form>
    </Layout>
  );
}
